// searchIndex.js · client-side search corpus.
//
// Three groups of records are indexed:
//   • PAGES     · every route the user can navigate to (built from MEGA_MENU)
//   • SYMBOLS   · canonical trading instruments with deep-links to the
//                 symbol explorer + market category page
//   • FAQS      · every FAQ question (built from FAQ_CATEGORIES)
//
// Each record exposes the fields required by GlobalSearch to score a query:
//   { kind, title, subtitle, to, keywords, Icon, badge, group }
//
// The matcher is a lightweight subsequence + token-overlap score. It is
// fast enough to run on every keystroke for 200+ records without any
// noticeable lag and avoids shipping a search library to the bundle.

import {
  Search as SearchIcon, FileText, BookOpen, Activity,
  LineChart, Coins, BarChart3, Bitcoin, Fuel, CandlestickChart,
  TrendingUp,
} from "lucide-react";
import { MEGA_MENU } from "@/data/megaMenu";
import { FAQ_CATEGORIES } from "@/data/site";

/* ============ PAGES · from mega menu ============
   Walks every column of every entry and emits one record per link.
   Spotlight CTAs and the top-level category landing pages are added
   manually so users can search for "trading" or "platforms" and land
   on the section overview. */
const TOP_LEVEL_PAGES = [
  { title: "Home",                   to: "/",                  Icon: SearchIcon, keywords: "home landing" },
  { title: "Account Comparison",     to: "/account-comparison", Icon: BarChart3,  keywords: "compare accounts spreads commission" },
  { title: "Live Spreads",           to: "/live-spreads",       Icon: Activity,   keywords: "live spreads pricing pips" },
  { title: "Trading Calculator",     to: "/calculator",         Icon: BarChart3,  keywords: "calculator pip margin swap" },
  { title: "Economic Calendar",      to: "/economic-calendar",  Icon: TrendingUp, keywords: "calendar news nfp cpi fomc events" },
  { title: "Symbol Specifications",  to: "/symbols",            Icon: FileText,   keywords: "symbol specs pip values lot size leverage" },
  { title: "FAQ",                    to: "/faq",                Icon: FileText,   keywords: "faq questions help support" },
  { title: "Trading Glossary",       to: "/glossary",           Icon: BookOpen,   keywords: "glossary terms definitions" },
  { title: "Contact",                to: "/contact",            Icon: FileText,   keywords: "contact email phone support" },
  { title: "Help Centre",            to: "/support",            Icon: FileText,   keywords: "support help" },
  { title: "Reviews",                to: "/reviews",            Icon: FileText,   keywords: "reviews clients testimonials" },
  { title: "Partner Programme",      to: "/partner",            Icon: FileText,   keywords: "partner ib introducer ambassador affiliate" },
  { title: "Regulation",             to: "/regulation",         Icon: FileText,   keywords: "regulation fsc mauritius license authorisation" },
  { title: "About OFinancial",       to: "/about",              Icon: FileText,   keywords: "about company story" },
  { title: "How It Works",           to: "/how-it-works",       Icon: FileText,   keywords: "how it works steps onboarding" },
  { title: "Legal Centre",           to: "/legal",              Icon: FileText,   keywords: "legal terms privacy policies" },
];

const pageRecords = (() => {
  const seen = new Set();
  const out = [];
  const push = (rec) => {
    const key = rec.to.split("#")[0] + "::" + rec.title.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ kind: "page", group: "Pages", ...rec });
  };
  TOP_LEVEL_PAGES.forEach(push);
  for (const entry of MEGA_MENU) {
    for (const col of entry.columns) {
      for (const it of col.items) {
        push({
          title: it.label,
          subtitle: it.tagline || `${entry.label} · ${col.heading}`,
          to: it.to,
          Icon: it.Icon,
          keywords: `${entry.label} ${col.heading} ${it.label} ${it.tagline || ""}`,
        });
      }
    }
  }
  return out;
})();

/* ============ SYMBOLS · canonical broker tradeable set ============
   We list the bread-and-butter instruments so users can type "EURUSD",
   "gold", "btc", "nasdaq" etc. and jump straight to the right asset's
   specs / spreads page. The deep-link goes to the SymbolExplorer with a
   pre-filtered category so it always lands on the right slice. */
const SYMBOLS = [
  // FX majors / minors
  { sym: "EURUSD",  name: "Euro · US Dollar",         cat: "Forex",    aliases: "euro dollar" },
  { sym: "GBPUSD",  name: "British Pound · US Dollar",cat: "Forex",    aliases: "pound cable sterling" },
  { sym: "USDJPY",  name: "US Dollar · Japanese Yen", cat: "Forex",    aliases: "yen" },
  { sym: "USDCHF",  name: "US Dollar · Swiss Franc",  cat: "Forex",    aliases: "swiss franc swissy" },
  { sym: "AUDUSD",  name: "Australian Dollar",        cat: "Forex",    aliases: "aussie" },
  { sym: "USDCAD",  name: "US Dollar · Canadian",     cat: "Forex",    aliases: "loonie cad" },
  { sym: "NZDUSD",  name: "New Zealand Dollar",       cat: "Forex",    aliases: "kiwi" },
  { sym: "EURGBP",  name: "Euro · British Pound",     cat: "Forex" },
  { sym: "GBPJPY",  name: "Pound · Yen",              cat: "Forex",    aliases: "gopher" },
  { sym: "EURJPY",  name: "Euro · Yen",               cat: "Forex" },
  // Metals
  { sym: "XAUUSD",  name: "Gold · US Dollar",         cat: "Metals",   aliases: "gold xau" },
  { sym: "XAGUSD",  name: "Silver · US Dollar",       cat: "Metals",   aliases: "silver xag" },
  { sym: "XPTUSD",  name: "Platinum",                 cat: "Metals",   aliases: "platinum" },
  { sym: "XPDUSD",  name: "Palladium",                cat: "Metals",   aliases: "palladium" },
  // Indices
  { sym: "NAS100",  name: "Nasdaq 100",               cat: "Indices",  aliases: "nasdaq us tech" },
  { sym: "SPX500",  name: "S&P 500",                  cat: "Indices",  aliases: "sp500 s&p" },
  { sym: "US30",    name: "Dow Jones",                cat: "Indices",  aliases: "dow jones djia" },
  { sym: "GER40",   name: "DAX 40",                   cat: "Indices",  aliases: "dax germany" },
  { sym: "UK100",   name: "FTSE 100",                 cat: "Indices",  aliases: "ftse uk" },
  { sym: "JPN225",  name: "Nikkei 225",               cat: "Indices",  aliases: "nikkei japan" },
  { sym: "FRA40",   name: "CAC 40",                   cat: "Indices",  aliases: "cac france" },
  { sym: "AUS200",  name: "ASX 200",                  cat: "Indices",  aliases: "asx australia" },
  // Energies
  { sym: "USOIL",   name: "WTI Crude Oil",            cat: "Energies", aliases: "wti crude oil" },
  { sym: "UKOIL",   name: "Brent Crude Oil",          cat: "Energies", aliases: "brent crude" },
  { sym: "NGAS",    name: "Natural Gas",              cat: "Energies", aliases: "natural gas" },
  // Crypto
  { sym: "BTCUSD",  name: "Bitcoin",                  cat: "Crypto",   aliases: "btc bitcoin" },
  { sym: "ETHUSD",  name: "Ethereum",                 cat: "Crypto",   aliases: "eth ether" },
  { sym: "SOLUSD",  name: "Solana",                   cat: "Crypto",   aliases: "sol solana" },
  { sym: "XRPUSD",  name: "Ripple",                   cat: "Crypto",   aliases: "xrp ripple" },
  { sym: "DOGUSD",  name: "Dogecoin",                 cat: "Crypto",   aliases: "doge dogecoin" },
  // Shares
  { sym: "AAPL",    name: "Apple Inc.",               cat: "Shares",   aliases: "apple stock" },
  { sym: "TSLA",    name: "Tesla",                    cat: "Shares",   aliases: "tesla elon" },
  { sym: "AMZN",    name: "Amazon",                   cat: "Shares",   aliases: "amazon" },
  { sym: "GOOGL",   name: "Alphabet",                 cat: "Shares",   aliases: "google alphabet" },
  { sym: "MSFT",    name: "Microsoft",                cat: "Shares",   aliases: "microsoft" },
  { sym: "NVDA",    name: "Nvidia",                   cat: "Shares",   aliases: "nvidia" },
  { sym: "META",    name: "Meta",                     cat: "Shares",   aliases: "meta facebook" },
];

const CAT_ICON = {
  Forex:    LineChart,
  Metals:   Coins,
  Indices:  BarChart3,
  Crypto:   Bitcoin,
  Energies: Fuel,
  Shares:   CandlestickChart,
};

const symbolRecords = SYMBOLS.map((s) => ({
  kind: "symbol",
  group: "Symbols",
  title: s.sym,
  subtitle: `${s.name} · ${s.cat}`,
  to: `/symbols?cat=${s.cat.toLowerCase()}&q=${s.sym}`,
  Icon: CAT_ICON[s.cat] || LineChart,
  badge: s.cat,
  keywords: `${s.sym} ${s.name} ${s.cat} ${s.aliases || ""}`,
}));

/* ============ FAQ · every question is a record ============ */
const faqRecords = FAQ_CATEGORIES.flatMap((cat) =>
  cat.items.map((it) => ({
    kind: "faq",
    group: "FAQ",
    title: it.q,
    subtitle: cat.label,
    to: `/faq#${it.id}`,
    Icon: FileText,
    keywords: `${it.q} ${cat.label} ${it.a.substring(0, 200)}`,
  }))
);

export const SEARCH_RECORDS = [...pageRecords, ...symbolRecords, ...faqRecords];

/* ============ Scorer ============
   Returns 0 for no-match, otherwise a positive score where higher is
   better. Heuristics (in order of weight):
     • title startsWith(query)               → +120
     • title contains(query) as whole word   → +80
     • title contains(query) anywhere        → +50
     • alias / keywords contains(query)      → +25
     • per-token: every query token must hit somewhere or score = 0
   Records of kind "symbol" get a small +10 bias so a typed ticker
   surfaces above accidental page matches. */
function score(rec, q) {
  if (!q) return 0;
  const query = q.toLowerCase().trim();
  if (!query) return 0;
  const title = (rec.title || "").toLowerCase();
  const hay   = `${title} ${(rec.subtitle || "").toLowerCase()} ${(rec.keywords || "").toLowerCase()}`;

  // Per-token AND filter · every word in the query must hit somewhere
  const tokens = query.split(/\s+/).filter(Boolean);
  for (const tk of tokens) {
    if (!hay.includes(tk)) return 0;
  }

  let s = 0;
  if (title.startsWith(query)) s += 120;
  else if (new RegExp(`\\b${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`).test(title)) s += 80;
  else if (title.includes(query)) s += 50;
  if ((rec.keywords || "").toLowerCase().includes(query)) s += 25;
  if (rec.kind === "symbol") s += 10;
  // Short titles win minor tiebreaker so "FAQ" beats "Frequently…"
  s -= Math.min(20, title.length / 4);
  return s;
}

export function searchRecords(q, { limit = 24 } = {}) {
  if (!q || !q.trim()) return [];
  const scored = [];
  for (const r of SEARCH_RECORDS) {
    const sc = score(r, q);
    if (sc > 0) scored.push({ rec: r, sc });
  }
  scored.sort((a, b) => b.sc - a.sc);
  return scored.slice(0, limit).map((x) => x.rec);
}
